<template>
  <div class="app-data-grid">
    <!-- Search Field -->
    <div v-if="showSearch" class="search-container">
      <div class="search-input-wrapper">
        <svg viewBox="0 0 24 24" width="20" height="20" class="search-icon">
          <path fill="currentColor" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="searchPlaceholder || t('common.search')"
          @input="onSearchInput"
        />
        <button v-if="searchQuery" @click="clearSearch" class="search-clear">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
          </svg>
        </button>
      </div>
    </div>    <ag-grid-vue
      v-if="!isMobile"
      ref="gridRef"
      data-testid="projects-grid"
      :class="['ag-theme-alpine', gridClass]"
      :columnDefs="processedColumnDefs" 
      :rowData="filteredRowData"
      :defaultColDef="mergedDefaultColDef" 
      :pagination="pagination"
      :paginationPageSize="currentPageSize"
      :paginationPageSizeSelector="pageSizeOptions"
      :suppressPaginationPanel="suppressPaginationPanel"
      :animateRows="animateRows"
      :getRowId="getRowId"
      :rowClassRules="rowClassRules"
      @grid-ready="onGridReady"
      @paginationChanged="onPaginationChanged"
      @selection-changed="onSelectionChanged"
      @row-clicked="onRowClicked"
      @cell-clicked="onCellClicked"
      :suppressMovableColumns="suppressMovableColumns" 
      :style="gridStyle"
      :domLayout="domLayout"
      v-bind="$attrs"
      theme="legacy"
    />

    <!-- Mobile card view — shown instead of AG-Grid on small viewports -->
    <div v-else class="mobile-card-view">
      <!-- Empty state -->
      <div v-if="filteredRowData.length === 0" class="mobile-empty-state">
        <svg viewBox="0 0 24 24" width="48" height="48" class="mobile-empty-icon">
          <path fill="currentColor" d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
        </svg>
        <p class="mobile-empty-text">{{ t('common.noData') }}</p>
      </div>

      <!-- Card list -->
      <div v-else class="mobile-cards">
        <div
          v-for="(item, mobileIdx) in mobilePagedData"
          :key="item.id ?? item.name ?? String(mobileIdx)"
          class="mobile-card"
          @click="onMobileCardClick(item)"
        >
          <!-- Header: avatar + title/subtitle + action buttons -->
          <div class="mobile-card__header">
            <div class="mobile-card__avatar" aria-hidden="true">{{ getMobileInitials(item) }}</div>
            <div class="mobile-card__info">
              <div class="mobile-card__title">{{ getMobileTitle(item) }}</div>
              <div v-if="mobileSubtitleField" class="mobile-card__subtitle">{{ getMobileSubtitle(item) }}</div>
            </div>
            <div class="mobile-card__actions" @click.stop>
              <button
                v-for="action in getVisibleActionsForItem(item)"
                :key="action.name"
                :class="['mobile-action-btn', action.class]"
                :title="action.label"
                :aria-label="action.label"
                @click.stop="onMobileActionClick(action, item)"
              >
                <i v-if="action.icon" :class="action.icon" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <!-- Detail rows derived from remaining column defs -->
          <div v-if="mobileDetailFields.length" class="mobile-card__details">
            <div
              v-for="field in mobileDetailFields"
              :key="field.key"
              class="mobile-card__detail-item"
            >
              <span class="mobile-card__detail-label">{{ field.label }}</span>
              <span class="mobile-card__detail-value">{{ getMobileFieldValue(item, field.colDef) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination && mobileTotalPages > 1" class="mobile-pagination">
        <button
          class="mobile-pagination__btn"
          :disabled="mobilePage <= 1"
          @click="mobilePage--"
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" />
          </svg>
          {{ t('common.previous') }}
        </button>
        <span class="mobile-pagination__info">{{ mobilePage }} / {{ mobileTotalPages }}</span>
        <button
          class="mobile-pagination__btn"
          :disabled="mobilePage >= mobileTotalPages"
          @click="mobilePage++"
        >
          {{ t('common.next') }}
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { AgGridVue } from 'ag-grid-vue3';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Types
interface ColumnDef {
  field?: string;
  headerName?: string;
  type?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  sortable?: boolean;
  filter?: boolean | string;
  cellRenderer?: string | Function;
  cellStyle?: any;
  valueFormatter?: Function;
  valueGetter?: Function;
  [key: string]: any;
}

interface Action {
  name: string;
  label: string;
  icon?: string;
  color?: string;
  class?: string;
  condition?: Function;
  handler: Function;
}

interface GridEvent {
  api: any;
  columnApi: any;
  type: string;
}

interface RowEvent {
  data: any;
  rowIndex: number;
  api: any;
  columnApi: any;
}

interface PaginationEvent {
  page: number;
  pageSize: number;
  totalPages: number;
  totalRows: number;
}

// Props
interface Props {
  rowData: any[];
  columnDefs: ColumnDef[];
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  suppressPaginationPanel?: boolean;
  animateRows?: boolean;
  suppressMovableColumns?: boolean;
  domLayout?: string;
  gridClass?: string;
  gridStyle?: string | object;
  actions?: Action[];
  defaultColDef?: Partial<ColumnDef>;
  showSearch?: boolean;
  searchPlaceholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  pagination: true,
  pageSize: 10,
  pageSizeOptions: () => [10, 20, 50, 100],
  suppressPaginationPanel: true,
  animateRows: true,
  suppressMovableColumns: false,
  domLayout: 'normal',
  gridClass: '',
  gridStyle: 'width: 100%; height: 100%;',
  actions: () => [],
  defaultColDef: () => ({}),
  showSearch: true,
  searchPlaceholder: ''
});

// Emits
interface Emits {
  'grid-ready': [event: GridEvent];
  'pagination-changed': [event: PaginationEvent];
  'selection-changed': [event: any[]];
  'row-clicked': [event: RowEvent];
  'action-clicked': [action: Action, data: any];
}

const emit = defineEmits<Emits>();

// Composables
const { t } = useI18n();

// Refs
const gridRef = ref(null);
const searchQuery = ref('');
const currentPageSize = ref(props.pageSize);

// Mobile viewport detection
const MOBILE_BREAKPOINT = 768;
const isMobile = ref(typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
const mobilePage = ref(1);

// Watch for prop changes to update local page size
watch(() => props.pageSize, (newSize: number) => {
  currentPageSize.value = newSize;
});

// Reset mobile page when search query changes
watch(searchQuery, () => {
  mobilePage.value = 1;
});
    
    // Default column configuration
    const baseDefaultColDef = {
      resizable: true,
      sortable: true,
      wrapText: false,
      autoHeight: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: '1.4'
      }
    };
    
    // Merge user-provided defaultColDef with base configuration
    const mergedDefaultColDef = computed(() => ({
      ...baseDefaultColDef,
      ...props.defaultColDef
    }));
    
    // Cell renderers for different data types
    const cellRenderers: Record<string, (params: any) => string> = {
      text: (params: any) => {
        if (params.value == null) return '';
        return `<span title="${params.value}">${params.value}</span>`;
      },
      
      boolean: (params: any) => {
        if (params.value == null) return '';
        const isTrue = params.value === true || params.value === 'true';
        return `
          <span class="boolean-cell ${isTrue ? 'boolean-true' : 'boolean-false'}">
            <i class="fas fa-${isTrue ? 'check' : 'times'}" aria-hidden="true"></i>
            ${isTrue ? t('common.yes') : t('common.no')}
          </span>
        `;
      },
        date: (params: any) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return `<span class="date-cell">${date.toLocaleDateString()}</span>`;
      },
      
      timestamp: (params: any) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        const dateStr = date.toLocaleDateString();
        const timeStr = date.toLocaleTimeString();
        return `<span class="timestamp-cell">${dateStr} ${timeStr}</span>`;
      },
      
      number: (params: any) => {
        if (params.value == null) return '';
        const value = Number(params.value);
        return `<span class="number-cell">${value.toLocaleString()}</span>`;
      },
      
      currency: (params: any) => {
        if (params.value == null) return '';
        const value = Number(params.value);
        return `<span class="currency-cell">${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value)}</span>`;
      }
    };
      // Actions cell renderer    
      const actionsRenderer = (params: any) => {
      if (!props.actions || props.actions.length === 0) return '';
      
      // Filter actions based on showCondition if provided
      const visibleActions = props.actions.filter((action: Action) => {
        if (!action.condition) return true;
        return action.condition(params.data);
      });
      
      if (visibleActions.length === 0) return '';
      
      const actionButtons = visibleActions.map((action: Action) => `
        <button 
          class="action-btn ${action.class || ''}" 
          data-action="${action.name}"
          title="${action.label || action.name}"
          aria-label="${action.label || action.name}"
        >
          <i class="${action.icon}" aria-hidden="true"></i>
        </button>
      `).join('');
      
      return `<div class="action-buttons" role="group" aria-label="Actions">${actionButtons}</div>`;
    };
      // Process column definitions to apply appropriate renderers
    const processedColumnDefs = computed(() => {
      const columns = [...props.columnDefs];
      
      // Apply cell renderers based on column type
      columns.forEach(column => {
        if (column.type && cellRenderers[column.type] && !column.cellRenderer) {
          column.cellRenderer = cellRenderers[column.type];
        }
        
        // Add appropriate CSS classes for different types
        if (column.type) {
          column.cellClass = column.cellClass || `${column.type}-cell`;
        }
        
        // Handle showCondition for columns by extending the cellRenderer
        if (column.showCondition) {
          const originalCellRenderer = column.cellRenderer;
          column.cellRenderer = (params: any) => {
            if (!column.showCondition(params.data)) {
              return '';
            }
            return originalCellRenderer ? originalCellRenderer(params) : params.value;
          };
        }
      });
      
      // Add actions column if actions are provided
      if (props.actions && props.actions.length > 0) {
        // Calculate dynamic width based on number of actions
        // Base width: 55px for header + padding
        // Per button: 44px (36px button + 8px gap)
        const numActions = props.actions.length;
        const baseWidth = 55;
        const buttonWidth = 44;
        const dynamicWidth = baseWidth + (numActions * buttonWidth);
        
        columns.push({
          headerName: t('common.actions'),
          field: 'actions',
          width: dynamicWidth,
          minWidth: dynamicWidth,
          maxWidth: dynamicWidth,
          resizable: false,
          sortable: false,
          filter: false,
          cellRenderer: actionsRenderer,
          pinned: 'right'
        });
      }
      
      return columns;
    });
    
    // Strip HTML tags from renderer output so the visible text can be searched.
    const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    const getColumnRawValue = (row: any, column: ColumnDef): any => {
      if (typeof column.valueGetter === 'function') {
        try {
          return column.valueGetter({ data: row, colDef: column });
        } catch {
          // Fall back to the field value when a value getter needs grid-only params.
        }
      }

      return column.field ? row[column.field] : undefined;
    };

    // Include text produced by formatters/renderers. Translated labels often only
    // exist in this display layer while the underlying row contains an enum value.
    const getColumnSearchText = (row: any, column: ColumnDef): string => {
      const rawValue = getColumnRawValue(row, column);
      const values: string[] = rawValue == null ? [] : [String(rawValue)];
      let formattedValue: any;

      if (typeof column.valueFormatter === 'function') {
        try {
          formattedValue = column.valueFormatter({
            value: rawValue,
            data: row,
            colDef: column
          });
          if (formattedValue != null) values.push(String(formattedValue));
        } catch {
          // A formatter may rely on AG Grid params unavailable during global search.
        }
      }

      if (typeof column.cellRenderer === 'function') {
        try {
          const renderedValue = column.cellRenderer({
            value: rawValue,
            valueFormatted: formattedValue,
            data: row,
            colDef: column
          });
          if (typeof renderedValue === 'string') values.push(stripHtml(renderedValue));
        } catch {
          // A renderer may rely on AG Grid params unavailable during global search.
        }
      }

      return values.join(' ');
    };

    // Filtered row data based on raw values and the text visible in grid cells.
    const filteredRowData = computed(() => {
      if (!searchQuery.value) return props.rowData;
      
      const query = searchQuery.value.trim().toLocaleLowerCase();
      if (!query) return props.rowData;

      return props.rowData.filter((row: any) => {
        const rawValueMatches = Object.values(row).some(value =>
          String(value).toLocaleLowerCase().includes(query)
        );
        if (rawValueMatches) return true;

        return props.columnDefs.some((column: ColumnDef) =>
          getColumnSearchText(row, column).toLocaleLowerCase().includes(query)
        );
      });
    });
    
    // Event handlers
    const onGridReady = (params: any) => {
      // Auto-size columns to fit
      if (params.api) {
        setTimeout(() => {
          params.api.sizeColumnsToFit();
          
          // Add data-testid to rows after grid is ready
          addDataTestIdToRows(params.api);
        }, 100);
      }
      emit('grid-ready', params);
    };
    
    // Function to add data-testid attributes to grid rows
    const addDataTestIdToRows = (api: any) => {
      setTimeout(() => {
        const rowElements = document.querySelectorAll('.ag-row');
        rowElements.forEach((rowElement) => {
          // Add basic row testid
          rowElement.setAttribute('data-testid', 'project-row');
          
          // Try to get the row data to add more specific attributes
          const rowIndexStr = rowElement.getAttribute('row-index');
          const rowIndex = rowIndexStr ? parseInt(rowIndexStr) : null;
          if (rowIndex !== null && !isNaN(rowIndex) && api) {
            const rowNode = api.getDisplayedRowAtIndex(rowIndex);
            if (rowNode && rowNode.data) {
              // Add project name as data attribute for easier testing
              if (rowNode.data.name) {
                rowElement.setAttribute('data-project-name', rowNode.data.name);
              }
              // Add project id as data attribute
              if (rowNode.data.id) {
                rowElement.setAttribute('data-project-id', rowNode.data.id);
              }
            }
          }
        });
      }, 200);
    };
    
    const onPaginationChanged = (params: any) => {
      // Update local page size when user changes it in the grid
      const newPageSize = params.api.paginationGetPageSize();
      if (newPageSize !== currentPageSize.value) {
        currentPageSize.value = newPageSize;
      }
      
      // Add data-testid to new rows when pagination changes
      addDataTestIdToRows(params.api);
      emit('pagination-changed', params);
    };
    
    const onSelectionChanged = (params: any) => {
      emit('selection-changed', params);
    };
      const onRowClicked = (params: any) => {
      // Check if the click originated from an action button
      const actionElement = params.event?.target?.closest('[data-action]');
      if (actionElement) {
        // Don't emit row-clicked if an action button was clicked
        return;
      }
      emit('row-clicked', params);
    };
    
    const onCellClicked = (params: any) => {
      // Check if this is an action button click
      const actionElement = params.event.target.closest('[data-action]');
      if (actionElement) {
        const actionName = actionElement.getAttribute('data-action');
        const action = props.actions.find((a: Action) => a.name === actionName);
        if (action) {
          emit('action-clicked', action, params.data);
        }
        // Prevent row click event from firing
        params.event.stopPropagation();
      }
    };
    
    // Search input handlers
    const onSearchInput = (event: any) => {
      searchQuery.value = event.target.value;
    };
    
    const clearSearch = () => {
      searchQuery.value = '';
    };
    
    // Expose grid API methods
    const getGridApi = () => {
      return gridRef.value?.api;
    };
    
    const getGridColumnApi = () => {
      return gridRef.value?.columnApi;
    };
    
    const sizeColumnsToFit = () => {
      const api = getGridApi();
      if (api) {
        api.sizeColumnsToFit();
      }
    };
    
    const autoSizeAllColumns = () => {
      const api = getGridApi();
      if (api) {
        api.autoSizeAllColumns();
      }
    };
    
    // Handle window resize
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT;
      if (!isMobile.value) {
        setTimeout(() => {
          sizeColumnsToFit();
        }, 100);
      }
    };

    // ── Mobile card view helpers ────────────────────────────────────────────

    // Column defs excluding the auto-added actions column and hidden columns
    const mobileColumns = computed(() =>
      props.columnDefs.filter(col => col.field && col.field !== 'actions' && !col.hide)
    );

    const mobileTitleField = computed(() => mobileColumns.value[0]?.field ?? '');
    const mobileSubtitleField = computed(() => mobileColumns.value[1]?.field ?? '');

    interface MobileDetailField {
      key: string;
      label: string;
      colDef: ColumnDef;
    }

    const mobileDetailFields = computed((): MobileDetailField[] =>
      mobileColumns.value.slice(2).map(col => ({
        key: col.field!,
        label: col.headerName ?? col.field!,
        colDef: col,
      }))
    );

    const mobilePagedData = computed(() => {
      if (!props.pagination) return filteredRowData.value;
      const start = (mobilePage.value - 1) * currentPageSize.value;
      return filteredRowData.value.slice(start, start + currentPageSize.value);
    });

    const mobileTotalPages = computed(() =>
      props.pagination
        ? Math.max(1, Math.ceil(filteredRowData.value.length / currentPageSize.value))
        : 1
    );

    // Resolve the display value for a cell using valueGetter → valueFormatter → cellRenderer → raw
    const getMobileFieldValue = (item: any, col: ColumnDef): string => {
      let rawValue: any;
      if (typeof col.valueGetter === 'function') {
        try { rawValue = col.valueGetter({ data: item, colDef: col }); } catch { /* fallback */ }
      }
      if (rawValue === undefined) rawValue = col.field ? item[col.field] : undefined;
      if (rawValue == null || rawValue === '') return '';

      if (typeof col.valueFormatter === 'function') {
        try {
          const formatted = col.valueFormatter({ value: rawValue, data: item, colDef: col });
          if (formatted != null) return String(formatted);
        } catch { /* fallback */ }
      }

      if (typeof col.cellRenderer === 'function') {
        try {
          const rendered = col.cellRenderer({ value: rawValue, data: item, colDef: col });
          if (typeof rendered === 'string') {
            return rendered.includes('<') ? stripHtml(rendered) : rendered;
          }
        } catch { /* fallback */ }
      }

      return String(rawValue);
    };

    const getMobileTitle = (item: any): string => {
      const col = mobileColumns.value[0];
      return col ? getMobileFieldValue(item, col) : '';
    };

    const getMobileSubtitle = (item: any): string => {
      const col = mobileColumns.value[1];
      return col ? getMobileFieldValue(item, col) : '';
    };

    const getMobileInitials = (item: any): string => {
      const title = getMobileTitle(item);
      const words = title.trim().split(/\s+/).filter((w: string) => w.length > 0);
      if (words.length >= 2) return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      if (words[0]) return words[0].substring(0, 2).toUpperCase();
      return '??';
    };

    const getVisibleActionsForItem = (item: any): Action[] =>
      props.actions.filter(action => !action.condition || action.condition(item));

    const onMobileCardClick = (item: any): void => {
      emit('row-clicked', { data: item, rowIndex: -1, api: null, columnApi: null } as any);
    };

    const onMobileActionClick = (action: Action, item: any): void => {
      emit('action-clicked', action, item);
    };

    // ── End mobile helpers ──────────────────────────────────────────────────

    // Row configuration functions
    const getRowId = (params: any): string => {
      return String(params.data?.id ?? params.data?.name ?? params.node?.rowIndex ?? -1);
    };
    
    const rowClassRules = {
      'project-row': () => true // Add project-row class to all rows
    };
    
      onMounted(() => {
      window.addEventListener('resize', handleResize);
    });    
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });
    
    // Watch for rowData changes to re-apply data-testid attributes
    watch(() => props.rowData, () => {
      mobilePage.value = 1;
      const api = getGridApi();
      if (api) {
        setTimeout(() => {
          addDataTestIdToRows(api);
        }, 300);
      }
    }, { deep: true });
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.app-data-grid {
  width: 100%;
  height: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

/* Search field styling */
.search-container {
  padding: 1rem 1.25rem;
  background-color: #ffffff;
  border-bottom: 1px solid #f1f3f5;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.625rem 0.875rem 0.625rem 2.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background-color: #f9fafb;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-weight: 400;
}

.search-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.search-input:focus {
  outline: none;
  border-color: #4f46e5;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.search-clear {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  transition: color 0.2s ease;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-clear:hover {
  color: #4f46e5;
  background-color: #f3f4f6;
}

/* Status badge styling (can be used for status columns) */
:deep(.status-badge) {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 14px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

:deep(.status-badge.status-active) {
  background-color: #d1fae5;
  color: #065f46;
}

:deep(.status-badge.status-draft) {
  background-color: #fef3c7;
  color: #92400e;
}

:deep(.status-badge.status-pending) {
  background-color: #dbeafe;
  color: #1e40af;
}

:deep(.status-badge.status-inactive) {
  background-color: #f3f4f6;
  color: #6b7280;
}

:deep(.status-badge.status-completed) {
  background-color: #d1fae5;
  color: #065f46;
}

:deep(.status-badge::before) {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: currentColor;
}

/* Avatar/Initial styling for client columns */
:deep(.client-cell) {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

:deep(.client-avatar) {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
}

:deep(.client-name) {
  font-weight: 500;
  color: #1f2937;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

/* Project ID/Code styling */
:deep(.project-code) {
  font-family: 'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 13px;
  color: #6b7280;
  font-weight: 400;
  letter-spacing: -0.02em;
}

/* Action buttons styling */
:deep(.action-buttons) {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
}

:deep(.action-btn) {
  background: #f3f4f6;
  color: #6b7280;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
  font-size: 14px;
}

:deep(.action-btn:hover) {
  background-color: #e5e7eb;
  color: #374151;
}

:deep(.action-btn.view-btn) {
  background-color: #f3f4f6;
  color: #6b7280;
}

:deep(.action-btn.view-btn:hover) {
  background-color: #e5e7eb;
  color: #374151;
}

:deep(.action-btn.edit-btn) {
  background-color: #f3f4f6;
  color: #6b7280;
}

:deep(.action-btn.edit-btn:hover) {
  background-color: #e5e7eb;
  color: #374151;
}

:deep(.action-btn.delete-btn) {
  background-color: #f3f4f6;
  color: #6b7280;
}

:deep(.action-btn.delete-btn:hover) {
  background-color: #fee2e2;
  color: #ef4444;
}

:deep(.action-btn.download-btn) {
  background-color: #f3f4f6;
  color: #6b7280;
}

:deep(.action-btn.download-btn:hover) {
  background-color: #d1fae5;
  color: #10b981;
}

/* Cell type styling */
:deep(.boolean-cell) {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 6px 14px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
}

:deep(.boolean-true) {
  background-color: #d1fae5;
  color: #065f46;
}

:deep(.boolean-false) {
  background-color: #fee2e2;
  color: #991b1b;
}

:deep(.date-cell) {
  color: #6b7280;
  font-size: 14px;
  font-weight: 400;
}

:deep(.timestamp-cell) {
  color: #6b7280;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
}

:deep(.number-cell) {
  font-weight: 500;
  text-align: right;
  color: #1f2937;
  font-size: 14px;
}

:deep(.currency-cell) {
  font-weight: 600;
  text-align: right;
  color: #059669;
  font-size: 15px;
  letter-spacing: -0.02em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-feature-settings: 'tnum';
}

/* Grid theme customization */
:deep(.ag-theme-alpine) {
  --ag-header-height: 56px;
  --ag-row-height: 72px;
  --ag-font-size: 14px;
  --ag-font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  --ag-header-background-color: #fafbfc;
  --ag-header-foreground-color: #6b7280;
  --ag-row-border-color: #f1f3f5;
  --ag-border-color: #e5e7eb;
  --ag-selected-row-background-color: #f9fafb;
  --ag-odd-row-background-color: #ffffff;
  --ag-background-color: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

:deep(.ag-header) {
  border-bottom: 2px solid #f1f3f5;
}

:deep(.ag-header-cell) {
  padding: 0 20px !important;
}

:deep(.ag-header-cell-label) {
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #6b7280;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

:deep(.ag-cell) {
  padding: 0 20px !important;
  display: flex;
  align-items: center;
  line-height: 1.5;
  color: #1f2937;
  font-size: 14px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-weight: 400;
}

:deep(.ag-row) {
  border-bottom: 1px solid #f1f3f5;
}

:deep(.ag-row:hover) {
  background-color: #f9fafb;
  cursor: pointer;
}

:deep(.ag-row:last-child) {
  border-bottom: none;
}

/* Mobile-specific optimizations */
@media (max-width: 768px) {
  .app-data-grid {
    margin: 0 -0.5rem;
    /* Allow mobile card view to expand to natural height instead of being
       constrained by a percentage of a fixed-height parent */
    height: auto;
  }

  .search-container {
    padding: 0.75rem 0.5rem;
    margin: 0 0.5rem;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  :deep(.ag-theme-alpine) {
    --ag-header-height: 44px;
    --ag-row-height: 40px;
    --ag-font-size: 13px;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }

  :deep(.ag-header-cell) {
    padding: 0 8px !important;
  }

  :deep(.ag-cell) {
    padding: 0 8px !important;
    line-height: 1.3 !important;
  }

  :deep(.ag-header-cell-text) {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Hide less important columns on small screens */
  :deep(.ag-header-cell[col-id="createdAt"]),
  :deep(.ag-cell[col-id="createdAt"]),
  :deep(.ag-header-cell[col-id="updatedAt"]),
  :deep(.ag-cell[col-id="updatedAt"]) {
    display: none !important;
  }

  /* Make action buttons more mobile-friendly */
  :deep(.action-buttons) {
    gap: 0.25rem;
    flex-wrap: wrap;
  }

  :deep(.action-btn) {
    min-width: 32px;
    min-height: 32px;
    padding: 6px;
  }

  /* Pagination adjustments for mobile */
  :deep(.ag-paging-panel) {
    padding: 8px 4px !important;
    font-size: 12px !important;
  }

  :deep(.ag-paging-page-summary-panel) {
    display: none !important;
  }

  :deep(.ag-paging-button) {
    min-width: 32px !important;
    min-height: 32px !important;
  }
}

@media (max-width: 480px) {
  .search-container {
    padding: 0.5rem;
    margin: 0;
  }

  :deep(.ag-theme-alpine) {
    --ag-header-height: 40px;
    --ag-row-height: 36px;
    --ag-font-size: 12px;
  }

  :deep(.ag-header-cell) {
    padding: 0 4px !important;
  }

  :deep(.ag-cell) {
    padding: 0 4px !important;
  }

  :deep(.ag-header-cell-text) {
    font-size: 11px;
  }

  /* Further hide columns on very small screens */
  :deep(.ag-header-cell[col-id="description"]),
  :deep(.ag-cell[col-id="description"]),
  :deep(.ag-header-cell[col-id="phone"]),
  :deep(.ag-cell[col-id="phone"]) {
    display: none !important;
  }

  :deep(.action-btn) {
    min-width: 28px;
    min-height: 28px;
    padding: 4px;
  }

  :deep(.action-btn svg) {
    width: 16px;
    height: 16px;
  }

  /* Stack pagination controls vertically on very small screens */
  :deep(.ag-paging-panel) {
    flex-direction: column !important;
    gap: 8px !important;
  }
}

/* Mobile-first responsive grid columns */
@media (max-width: 640px) {
  /* Only show most essential columns */
  :deep(.ag-header-cell:not([col-id="nom"]):not([col-id="prenom"]):not([col-id="fonction"]):not([col-id="actions"])),
  :deep(.ag-cell:not([col-id="nom"]):not([col-id="prenom"]):not([col-id="fonction"]):not([col-id="actions"])) {
    display: none !important;
  }

  /* Expand essential columns to take full width */
  :deep(.ag-header-cell[col-id="nom"]),
  :deep(.ag-cell[col-id="nom"]) {
    flex: 2 !important;
    min-width: 120px !important;
  }

  :deep(.ag-header-cell[col-id="prenom"]),
  :deep(.ag-cell[col-id="prenom"]) {
    flex: 1.5 !important;
    min-width: 100px !important;
  }

  :deep(.ag-header-cell[col-id="fonction"]),
  :deep(.ag-cell[col-id="fonction"]) {
    flex: 1.5 !important;
    min-width: 100px !important;
  }

  :deep(.ag-header-cell[col-id="actions"]),
  :deep(.ag-cell[col-id="actions"]) {
    flex: 1 !important;
    min-width: 80px !important;
  }
}

/* ── Mobile card view ────────────────────────────────────────────────────── */

.mobile-card-view {
  width: 100%;
  /* Ensure content is accessible via touch scroll on iOS */
  -webkit-overflow-scrolling: touch;
}

.mobile-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 1rem;
  text-align: center;
}

.mobile-empty-icon {
  color: #9ca3af;
  margin-bottom: 0.75rem;
}

.mobile-empty-text {
  color: #6b7280;
  font-size: 0.9375rem;
  margin: 0;
}

.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.625rem 0;
}

.mobile-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.mobile-card:active {
  transform: scale(0.99);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.mobile-card__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.mobile-card__avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-weight: 600;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.mobile-card__info {
  flex: 1;
  min-width: 0;
}

.mobile-card__title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.mobile-card__subtitle {
  margin-top: 0.1875rem;
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-card__actions {
  display: flex;
  gap: 0.375rem;
  flex-shrink: 0;
  align-items: center;
}

.mobile-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 0.5rem;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.15s ease, color 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.mobile-action-btn:active {
  background: #e5e7eb;
}

/* Reuse the same hover colours as the desktop action-btn overrides */
.mobile-action-btn.delete-btn { color: #6b7280; }
.mobile-action-btn.delete-btn:active { background: #fee2e2; color: #ef4444; }
.mobile-action-btn.edit-btn:active { background: #d1fae5; color: #059669; }
.mobile-action-btn.view-btn:active { background: #dbeafe; color: #2563eb; }
.mobile-action-btn.approve-btn:active { background: #d1fae5; color: #059669; }
.mobile-action-btn.reject-btn:active { background: #fee2e2; color: #ef4444; }

.mobile-card__details {
  margin-top: 0.625rem;
  padding-top: 0.625rem;
  border-top: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.mobile-card__detail-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.mobile-card__detail-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
}

.mobile-card__detail-value {
  font-size: 0.8125rem;
  color: #374151;
  text-align: right;
  word-break: break-word;
  min-width: 0;
}

/* Pagination */
.mobile-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 0 0.25rem;
  margin-top: 0.25rem;
  border-top: 1px solid #e5e7eb;
}

.mobile-pagination__btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.mobile-pagination__btn:active:not(:disabled) {
  background: #f3f4f6;
}

.mobile-pagination__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.mobile-pagination__info {
  font-size: 0.8125rem;
  color: #6b7280;
}

/* On very small screens, stack detail items vertically */
@media (max-width: 360px) {
  .mobile-card__detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
  }

  .mobile-card__detail-value {
    text-align: left;
  }

  .mobile-pagination {
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
  }

  .mobile-pagination__btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
